import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useState } from 'react';

import { log } from '@/app/shared/utils/logger';
import {
  DailyEnergyCard,
  TopCard,
  PlusButton,
  ProgressSection,
  SettlementImage,
} from '@app/shared/ui/molecules/Home';
import { Page } from '@app/routes';
import { UserProgress } from '@app/store/slices/userProgress/userProgressTypes';
import { homeEllipseImage, homeRectangleImage, defaultPandaImage } from '@app/assets';
import { usePageTimer } from '@app/shared/hooks/usePageTimer';
import { useLiveCountdown } from '@app/shared/hooks/useLiveCountdown';
import { usePurchaseSettlement } from '@/app/shared/hooks/usePurchaseSettlement';
import { LOCAL_STORAGE, ONE_DAY_MS, ONE_WEEK_MS } from '@app/shared/constants/common';
import { Modal } from '@app/shared/ui/organisms/Common';
import { Button, Paragraph } from '@app/shared/ui/atoms/Common';
import { cn } from '@app/shared/utils/common';
import { Settlement } from '@app/store/slices/settlements/settlementsTypes';
import notification from '@app/shared/utils/notification';

interface TopSectionProps {
  userProgress?: UserProgress | null;
  onPlusBtnClick: () => void;
  settlements?: Settlement[];
}

export function TopSection({ userProgress, onPlusBtnClick, settlements }: TopSectionProps) {
  const navigate = useNavigate();
  const [isEnergyGateOpen, setIsEnergyGateOpen] = useState(false);
  const [isCardGateOpen, setIsCardGateOpen] = useState(false);

  const { purchaseSettlement, isPurchasingSettlement } = usePurchaseSettlement();

  const { isExpired: isCardProbationExpired } = usePageTimer(LOCAL_STORAGE.cardUsedAt, ONE_WEEK_MS);
  const { isExpired: isEnergyProbationExpired } = usePageTimer(
    LOCAL_STORAGE.energyUsedAt,
    ONE_WEEK_MS
  );

  const { lastAt } = usePageTimer(LOCAL_STORAGE.cardDailyUseAt, ONE_DAY_MS);
  const { isExpired: isCardPageAvailable, formattedLeft } = useLiveCountdown(
    lastAt ?? 0,
    ONE_DAY_MS
  );

  const energySettlement = settlements?.[0];
  const cardSettlement = settlements?.[2];

  return (
    <section className="relative flex justify-center">
      <img src={homeEllipseImage} alt="ellipse" className="absolute bottom-0 w-[220px]" />
      <img src={homeRectangleImage} alt="rectangle" className="absolute bottom-0 w-full" />
      <img
        src={userProgress?.icon ?? defaultPandaImage}
        alt="panda"
        className="relative max-h-[256px]"
      />

      <div
        onClick={() => {
          const isBlocked = !energySettlement?.isPurchased && isEnergyProbationExpired;

          if (isBlocked) {
            setIsEnergyGateOpen(true);
            return;
          }

          void navigate(Page.DAILY_ENERGY);
        }}
        className="absolute left-0 top-0"
      >
        <DailyEnergyCard />
      </div>

      <div
        onClick={() => {
          if (isCardPageAvailable) {
            const isBlocked = !cardSettlement?.isPurchased && isCardProbationExpired;

            if (isBlocked) {
              setIsCardGateOpen(true);
              return;
            }

            void navigate(Page.DAILY_CARD_DESCRIPTION);
          }

          notification.info(t('common.available_after', { value: formattedLeft }));
        }}
        className={cn('absolute right-0 top-0', !isCardPageAvailable && 'grayscale')}
      >
        <TopCard />
        {!isCardPageAvailable && (
          <span className="text-xs absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            {formattedLeft}
          </span>
        )}
      </div>

      <div className="absolute right-0 top-[65px]">
        <PlusButton onClick={onPlusBtnClick} />
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <ProgressSection userProgress={userProgress} />
      </div>

      {!!energySettlement && (
        <Modal
          isOpen={isEnergyGateOpen}
          onOpenChange={setIsEnergyGateOpen}
          featuredImg={
            <SettlementImage
              imageUrl={energySettlement.imageUrl ?? ''}
              name={energySettlement.name}
              className="grayscale static size-[150px]"
            />
          }
          contentClassName="bg-background shadow-blue"
        >
          <Paragraph>
            {t('daily_energy.buy_modal_text', { title: energySettlement.name })}
          </Paragraph>

          <Button
            onClick={async () => {
              try {
                await purchaseSettlement(energySettlement, () => navigate(Page.DAILY_ENERGY));
              } catch (error: unknown) {
                void log({ level: 'error', message: 'purchaseSettlement error', error });
              }
            }}
            disabled={isPurchasingSettlement}
          >
            {isPurchasingSettlement ? t('common.wait') : t('common.open')}
          </Button>
        </Modal>
      )}

      {!!cardSettlement && (
        <Modal
          isOpen={isCardGateOpen}
          onOpenChange={setIsCardGateOpen}
          featuredImg={
            <SettlementImage
              imageUrl={cardSettlement.imageUrl ?? ''}
              name={cardSettlement.name}
              className="grayscale static size-[150px]"
            />
          }
          contentClassName="bg-background shadow-blue"
        >
          <Paragraph>
            {t('daily_card_result.buy_modal_text', { title: cardSettlement.name })}
          </Paragraph>

          <Button
            onClick={async () => {
              try {
                await purchaseSettlement(energySettlement, () =>
                  navigate(Page.DAILY_CARD_DESCRIPTION)
                );
              } catch (error: unknown) {
                void log({ level: 'error', message: 'purchaseSettlement error', error });
              }
            }}
            disabled={isPurchasingSettlement}
          >
            {isPurchasingSettlement ? t('common.wait') : t('common.open')}
          </Button>
        </Modal>
      )}
    </section>
  );
}
